import { cx } from 'emotion';
import {
  CSSProperties,
  ComponentType,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
  CALL_BETS,
  CALL_BETS_DECISION,
  GAME_RESULT,
  INSURANCE_CALLS,
  NO_MORE_BETS,
  WAIT_FOR_NEXT_GAME,
} from '@ezugi/constants';
import { useRootSize } from '@ezugi/hooks';
import { CssTransition } from '@ezugi/primitives/src/CssTransition';

import CallBetsToolbarBootstrap, { CallBetsToolbarProps } from '../../components/CallBetsToolbar';
import ChipSelector from '../../components/ChipSelector';
import ProgressBarBootstrap, { ConnectedProgressBarProps } from '../../components/ProgressBar';
import DefaultBetActions, { BetActionsProps } from '../../features/bet-toolbar/BetActions';
import {
  canPlaceBetsSelector,
  desktopBetToolbarConfigSelector,
  desktopBetToolbarPositionSelector,
  roundIdSelector,
  roundStatusSelector,
} from '../../store/selectors';
import {
  containerClassName,
  widgetsWrapperClassName,
  betToolbarBackgroundClassName,
  betToolbarClassName,
  chipSelectorClassName,
  hiddenBetToolbarBackground,
  progressBarClassName,
  betActionsWrapper,
  progressBarWrapper,
} from './style.css';

const GAMET_TOOLS_WIDTH_RATIO = 4.3;

export type GameToolsProps = {
  style?: CSSProperties;
  trailingNMB?: boolean;
  children?: ReactNode;
  hidePlaceBets?: boolean;
  components?: {
    ProgressBar?: ComponentType<ConnectedProgressBarProps>;
    CallBetsToolbar?: ComponentType<CallBetsToolbarProps>;
    BetActions?: ComponentType<BetActionsProps>;
  };
  /**
   * round statuses when `CallBetsToolbar` is visible
   * @default [ CALL_BETS, CALL_BETS_DECISION, INSURANCE_CALLS ]
   */
  callBetsToolbarStates?: string[],
};

function GameTools({
  style,
  trailingNMB,
  children,
  components,
  hidePlaceBets = false,
  callBetsToolbarStates = [ CALL_BETS, CALL_BETS_DECISION, INSURANCE_CALLS ],
}: GameToolsProps) {
  const ProgressBar = components?.ProgressBar ?? ProgressBarBootstrap;
  const CallBetsToolbar = components?.CallBetsToolbar ?? CallBetsToolbarBootstrap;

  const BetActions = components?.BetActions ?? DefaultBetActions;

  const canPlaceBets = useSelector(canPlaceBetsSelector);
  const roundStatus = useSelector(roundStatusSelector);
  const roundId = useSelector(roundIdSelector);

  const { width: rootWidth } = useRootSize();

  const hasBetToolbarBackground = useSelector(desktopBetToolbarConfigSelector);
  const desktopBetToolbarPosition = useSelector(
    desktopBetToolbarPositionSelector
  );

  const roundStatusRef = useRef(roundStatus);
  const roundIdRef = useRef<number | undefined | null>(null);
  const isCallBets = callBetsToolbarStates.includes(roundStatus);
  const isNMB = [ NO_MORE_BETS ].includes(roundStatus);
  const showCallBetsProgress = [ CALL_BETS, INSURANCE_CALLS ].includes(
    roundStatus
  );
  const [ showProgressBar, updateShowProgressBar ] = useState(
    canPlaceBets || showCallBetsProgress
  );

  const { betToolbarXOffset, betToolbarYOffset } = desktopBetToolbarPosition;

  useEffect(() => {
    let handle: ReturnType<typeof setTimeout>;
    function update() {
      updateShowProgressBar(
        canPlaceBets || showCallBetsProgress
      );
    }
    if (
      trailingNMB
      && isNMB
      && roundStatusRef.current
      && roundStatusRef.current !== NO_MORE_BETS
      && roundId !== roundIdRef.current
    ) {
      roundIdRef.current = roundId;
      handle = setTimeout(() => {
        update();
      }, 3000);
    } else {
      update();
    }
    return () => clearTimeout(handle);
  }, [ canPlaceBets, showCallBetsProgress, trailingNMB, roundId, isNMB ]);

  useEffect(() => {
    roundStatusRef.current = roundStatus;
  }, [ roundStatus ]);

  const isHiddenPlaceBets = hidePlaceBets || (!canPlaceBets || isCallBets);

  return (
    <div className={containerClassName} data-e2e="game-tools" style={style}>
      <Suspense fallback={null}>
        <div
          style={{
            width: rootWidth / GAMET_TOOLS_WIDTH_RATIO,
            ...(betToolbarXOffset ? { left: betToolbarXOffset } : {}),
            ...(betToolbarYOffset ? { top: betToolbarYOffset } : {}),
          }}
          className={cx(widgetsWrapperClassName, {
            [betToolbarBackgroundClassName]: hasBetToolbarBackground,
            [hiddenBetToolbarBackground]: [
              NO_MORE_BETS,
              GAME_RESULT,
              CALL_BETS_DECISION,
              WAIT_FOR_NEXT_GAME,
            ].includes(roundStatus),
          })}
        >
          {children}

          <CssTransition effect="fade" duration={400} visible={showProgressBar}>
            <div className={progressBarWrapper}>
              <ProgressBar className={progressBarClassName} />

              {isCallBets && (
                <CallBetsToolbar />
              )}
            </div>
          </CssTransition>

          <CssTransition effect="fade" duration={400} visible={!isHiddenPlaceBets}>
            <div className={betActionsWrapper}>
              <ChipSelector
                data-e2e="chip-selector"
                id="chip-selector"
                className={chipSelectorClassName}
              />

              <BetActions className={betToolbarClassName} />
            </div>
          </CssTransition>

          <div id="game-tools-extras" style={{ width: '100%' }}>{children}</div>
        </div>
      </Suspense>
    </div>
  );
}

export default GameTools;
